import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useGetProjectQuery,
  useGetBoardWorkflowTasksQuery,
  useGetTaskStatusesQuery,
  useGetBoardUsersQuery,
  useGetTimelineQuery,
  useCreateTimelineRowMutation,
  usePatchTimelineRowMutation,
} from 'app/services/projectBoardApi';
import type { ITimelineRow, IWorkflowTask } from 'models';
import { useTabAccess } from '../hooks/useProjectBoardData';
import { DatePickerPopover } from './DateTimePickers';

/**
 * TIMELINE TAB (`#timeline` — `timeline/view/timeline.js` + timeline.tmpl /
 * timeline_item.tmpl), inventory §3.10 / parity 88–98.
 *
 * Grid: Workflow Task | Scheduled (Start, By, Complete, By, Days) | Actual
 * (same 5) + the footer legend. One row per CMS workflow task in catalog
 * order, even when no timeline record exists yet (an unsaved placeholder
 * {project_id, task_id}). Scheduled Start/Complete are editable typed dates;
 * first edit of a never-persisted row POSTs (creates the record), later
 * edits PATCH only the changed field (TimelineModel autosave — the LIVE
 * project-local shadow `workflow_popup/timeline/model/timeline.js`). Actual
 * columns + all By columns are read-only Workflow-Manager outputs from the
 * server. Days = (complete − start)/86400000 + 1 client-side, blank unless
 * both dates present (parity 92).
 *
 * DATEPICKER DECISION (risk 6 / parity 95 — REVISED in the QA fix run): in
 * Backbone, `focus input` (timeline_item.js:17,58-63) attaches jQuery-UI's
 * datepicker ({dateFormat:'yy-mm-dd', changeMonth, changeYear}) — an
 * attachment that only works when jquery-ui's `draggable` bundle loaded
 * first (a direct `#timeline` deep-link THROWS on focus), but on normal
 * navigation users had a working calendar. RESTORED as a React popover
 * (DateTimePickers.tsx) on the two SCHEDULED inputs: opens on focus/click,
 * picking writes the legacy widget's `yyyy-mm-dd` output through the same
 * commit path as typing-then-blur; deep links no longer throw. The legacy
 * `focus input` binding technically also hit the readonly actual_* inputs
 * (letting the picker overwrite Workflow-Manager outputs through the
 * catch-all change handler) — NOT restored: the port treats Actual columns
 * as read-only WM outputs (see above), and typed entry there was already
 * impossible in both stacks.
 *
 * Workflow interplay: WorkflowPopup Done invalidates the `Timeline` tag →
 * this tab re-renders from the shared RTK cache (the legacy shares the
 * stateStorage 'timelineCollection' instance with the popup, so its rows
 * visibly update after Done).
 *
 * Keep-alive parity (parity 94): the tab mounts once (BoardTabs) and the RTK
 * cache prevents refetches on re-open.
 *
 * [NEG] honored: `collections.Users` → /api/v1.0/mfg/1/users/ never fetched
 * (users come from /api/v1.0/users/ — fetched for request parity, the
 * arranged map is never consumed by the template); the `popupClosed`
 * listener (no emitter); the shadowed `timeline/collection/timeline.js` and
 * `mvc/model/critical_task.js` duplicates; the `.active`-on-parent-li no-op.
 */

/** calculateDays: (complete − start)/86400000 + 1; '' unless both present. */
const calcDays = (start: string | null | undefined, end: string | null | undefined): string => {
  if (!start || !end) return '';
  const days =
    (new Date(end).getTime() - new Date(start).getTime()) / 86400000 + 1;
  return Number.isNaN(days) ? '' : String(days);
};

/**
 * isValidSchedDate (sync K — timeline_item.js): strict canonical
 * `yyyy-mm-dd` — the stored/PATCHed format in BOTH stacks (jQuery-UI
 * `yy-mm-dd` output there, DatePickerPopover output / DRF ISO echo here; the
 * `yyyy.mm.dd` placeholder never matched either stack's canonical value) —
 * plus a real-calendar round-trip, because Date's own parser is too lenient
 * (e.g. new Date('66') parses to a real date instead of Invalid Date).
 */
const isValidSchedDate = (value: string): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!match) return false;
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

/** validateDateTime: '-' → '.' in the actual-date placeholder text. */
const dotted = (value: string | null | undefined): string =>
  value ? value.replace(/-/g, '.') : '';

const SUB_HEADER = (
  <div className="row">
    <div className="col-xs-2" />
    {[0, 1].map((side) => (
      <div className="col-xs-4" key={side}>
        <div className="row">
          <div className="col-xs-4">
            <div>
              <h3 className="col-heading">Start</h3>
            </div>
          </div>
          <div className="no-padding col-xs-1">
            <div>
              <h3 className="col-heading">By</h3>
            </div>
          </div>
          <div className="col-xs-4">
            <div>
              <h3 className="col-heading">Complete</h3>
            </div>
          </div>
          <div className="no-padding col-xs-1">
            <div>
              <h3 className="col-heading">By</h3>
            </div>
          </div>
          <div className="col-xs-1">
            <div>
              <h3 className="col-heading">Days</h3>
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const TimelineTab = ({ projectId }: { projectId: number }) => {
  const { data: project } = useGetProjectQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });
  const { data: tasks } = useGetBoardWorkflowTasksQuery();
  // Fetched for request parity (legacy Promise.all) — names unused by the grid.
  const { data: statuses } = useGetTaskStatusesQuery();
  const { data: users } = useGetBoardUsersQuery(); // fn['fetch.user']
  const { data: timeline } = useGetTimelineQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });
  const permission = useTabAccess(projectId, 'timeline');

  // One row per CMS task in catalog order; missing records become unsaved
  // placeholders (render() adds an unsaved TimelineModel to the collection).
  const rows = useMemo(() => {
    if (!tasks) return [];
    return tasks.map((task) => ({
      task,
      row:
        timeline?.find((r) => r.task_id === task.id) ??
        ({ project_id: projectId, task_id: task.id } as ITimelineRow),
    }));
  }, [tasks, timeline, projectId]);

  if (!project || !tasks || !statuses || !users || !timeline) return null;

  return (
    <>
      <div className="content row">
        <div className="content-item col-xs-12">
          <div className="col-xs-12">
            <div className="timeline-table-heading  row" id="workflow_tasks_list">
              <div className="col-xs-2">
                <h3>Workflow Task</h3>
              </div>
              <div className="sched-col col-xs-4">
                <h3>Scheduled</h3>
              </div>
              <div className="actual-col col-xs-4">
                <h3>Actual</h3>
              </div>
            </div>
            {SUB_HEADER}
            <div className="timeline-table-content">
              {rows.map(({ task, row }) => (
                <TimelineItemRow
                  key={task.id}
                  projectId={projectId}
                  task={task}
                  row={row}
                  permission={permission}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="note col-xs-12">
        <div className="col-xs-offset-2 col-xs-4">
          <div className="row">
            <h5>Inputted by User as planned</h5>
          </div>
        </div>
        <div className="col-xs-4">
          <div className="row">
            <h5>Outputted by Workflow Manager</h5>
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * TimelineItemView (timeline_item.tmpl, `div.row` per task). Scheduled date
 * inputs commit on change (blur-if-changed): rows without an id POST the
 * full row (create), persisted rows PATCH the single changed field; the
 * server echo (By initials, sch_days, …) merges into the shared cache.
 */
const TimelineItemRow = ({
  projectId,
  task,
  row,
  permission,
}: {
  projectId: number;
  task: IWorkflowTask;
  row: ITimelineRow;
  permission: boolean;
}) => {
  const [createRow] = useCreateTimelineRowMutation();
  const [patchRow] = usePatchTimelineRowMutation();

  // Sibling-bound reads (sync K): legacy clampSchedDate/changeDateInput read
  // the OTHER sched input's displayed .val() at interaction time; the RTK
  // cache row lags a just-committed PATCH by a round-trip, so the latest
  // committed edit overlays it — same instant-read semantics.
  const [edits, setEdits] = useState<
    Partial<Record<'sch_start_date' | 'sch_end_date', string | null>>
  >({});
  const bound = (name: 'sch_start_date' | 'sch_end_date'): string =>
    (edits[name] !== undefined
      ? edits[name]
      : (row[name] as string | null | undefined)) ?? '';

  const commit = (name: 'sch_start_date' | 'sch_end_date', value: string | null) => {
    // `value` arrives already normalized/clamped by SchedDateInput (sync-K
    // guards, timeline_item.js changeInput): invalid typed input → null —
    // which also subsumes the older ''→null QA contract (DRF DateField 400s
    // on "" and the clear is silently lost) — valid input → clamped into the
    // sibling bound. The persisted value is therefore the clamped one.
    setEdits((e) => ({ ...e, [name]: value }));
    if (row.id != null) {
      patchRow({ projectId, id: row.id, body: { [name]: value } });
    } else {
      // First edit creates the record (Backbone POSTs the full attrs).
      createRow({
        projectId,
        body: { project_id: projectId, task_id: task.id, [name]: value },
      });
    }
  };

  return (
    <div className="row">
      <div className="col-xs-2">
        <span className="main-item task-col">
          <span> {task.name} </span>
        </span>
      </div>
      <div className="sched-col col-xs-4">
        <div className="row">
          <div className="col-xs-4 start-col">
            <span className="sched-start ">
              <SchedDateInput
                className="start-sched-input date-input"
                name="sch_start_date"
                value={(row.sch_start_date as string | null | undefined) ?? ''}
                disabled={!permission}
                maxBound={bound('sch_end_date')}
                onCommit={(v) => commit('sch_start_date', v)}
              />
            </span>
          </div>
          <div className="col-xs-1 start-by-col no-padding">
            <span className="sched-by  sched-stared-by">{row.sch_started_by ?? ''}</span>
          </div>
          <div className="col-xs-4 complete-col">
            <span className="sched-complete ">
              <SchedDateInput
                className="complete-sched-input date-input"
                name="sch_end_date"
                value={(row.sch_end_date as string | null | undefined) ?? ''}
                disabled={!permission}
                minBound={bound('sch_start_date')}
                onCommit={(v) => commit('sch_end_date', v)}
              />
            </span>
          </div>
          <div className="col-xs-1 complete-by-col no-padding">
            <span className="sched-by sched-complete-by">{row.sch_completion_by ?? ''}</span>
          </div>
          <div className="col-xs-1 duration-col">
            <span className=" sched-duration right">
              {calcDays(row.sch_start_date, row.sch_end_date)}
            </span>
          </div>
        </div>
      </div>
      <div className="actual-col col-xs-4">
        <div className="row">
          <div className="col-xs-4 start-col">
            <span className={` actual-start ${row.actual_start_date ? ' ' : ' empty '}`}>
              <input
                className="start-actual-input date-input"
                type="date"
                tabIndex={-1}
                name="actual_start_date"
                value={(row.actual_start_date as string | null | undefined) ?? ''}
                placeholder="---"
                required
                readOnly
                onChange={() => undefined}
              />
              {row.actual_start_date ? (
                <span className="date-placeholder value">{dotted(row.actual_start_date)}</span>
              ) : (
                <span className="date-placeholder">---</span>
              )}
            </span>
          </div>
          <div className="col-xs-1 start-by-col no-padding">
            <span className="actual-by start-by-col">{row.started_by ?? ''}</span>
          </div>
          <div className="complete-col col-xs-4">
            <span className={`actual-complete${row.actual_end_date ? '' : ' empty '}`}>
              <input
                className="complete-actual-input date-input"
                type="date"
                tabIndex={-1}
                name="actual_end_date"
                value={(row.actual_end_date as string | null | undefined) ?? ''}
                required
                readOnly
                onChange={() => undefined}
              />
              {row.actual_end_date ? (
                <span className="date-placeholder value">{dotted(row.actual_end_date)}</span>
              ) : (
                <span className="date-placeholder">---</span>
              )}
            </span>
          </div>
          <div className="col-xs-1 complete-by-col no-padding">
            <span className=" actual-by complete-by-col">{row.completion_by ?? ''}</span>
          </div>
          <div className="col-xs-1 duration-col">
            <span className=" actual-duration right">
              {calcDays(row.actual_start_date, row.actual_end_date)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Scheduled-date input: typed entry commits on blur-if-changed; focus/click
 * opens the calendar popover (datepicker decision in the header comment) and
 * a pick commits through the same path.
 *
 * Sync-K guards (timeline_item.js changeInput/clampSchedDate/
 * normalizeSchedDate): a committed value that fails isValidSchedDate
 * normalizes to null (field clears); a valid value clamps into the sibling
 * bound — the EDITED field moves to the other bound (complete < start →
 * complete = start; start > complete → start = complete) — before the
 * PATCH/POST, so the persisted value is the clamped one. The popover gets
 * the same bounds as jQuery-UI minDate/maxDate (out-of-range days
 * unselectable), matching legacy changeDateInput.
 */
const SchedDateInput = ({
  className,
  name,
  value,
  disabled,
  minBound,
  maxBound,
  onCommit,
}: {
  className: string;
  name: string;
  value: string;
  disabled: boolean;
  /** Sibling sched date: clamp floor + picker minDate (the complete input). */
  minBound?: string;
  /** Sibling sched date: clamp ceiling + picker maxDate (the start input). */
  maxBound?: string;
  onCommit: (value: string | null) => void;
}) => {
  const [draft, setDraft] = useState(value ?? '');
  const [pickerOpen, setPickerOpen] = useState(false);
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (document.activeElement !== ref.current) setDraft(value ?? '');
  }, [value]);

  const openPicker = () => {
    if (!disabled) setPickerOpen(true);
  };

  const commitDraft = (raw: string) => {
    let final: string | null;
    if (isValidSchedDate(raw)) {
      final = raw;
      // clampSchedDate — only valid values clamp; bounds are committed
      // canonical dates (or ''), compared as Dates like legacy.
      if (minBound && new Date(final) < new Date(minBound)) final = minBound;
      if (maxBound && new Date(final) > new Date(maxBound)) final = maxBound;
    } else {
      final = null; // normalizeSchedDate: invalid → cleared
    }
    setDraft(final ?? '');
    // Backbone's model.set is a no-op when the normalized/clamped result
    // equals the stored value — no autosave fires; mirror that.
    if ((final ?? '') !== (value ?? '')) onCommit(final);
  };

  return (
    <>
      <input
        ref={ref}
        className={className}
        type="text"
        name={name}
        value={draft}
        autoComplete="off"
        placeholder="yyyy.mm.dd"
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={openPicker}
        onClick={openPicker}
        onBlur={() => {
          if (draft !== (value ?? '')) commitDraft(draft);
        }}
      />
      {pickerOpen && ref.current && (
        <DatePickerPopover
          anchor={ref.current}
          value={draft}
          minDate={minBound}
          maxDate={maxBound}
          onPick={(v) => {
            commitDraft(v);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </>
  );
};

export default TimelineTab;
